import React, { useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Package,
  TrendingUp,
  Clock,
  XCircle,
  DollarSign,
  CalendarIcon,
  Download,
  FileText,
  FileSpreadsheet,
  File,
  ArrowUpFromLine,
  ArrowDownToLine,
  BarChart3,
  Pencil,
  Check,
  X,
  Trash2,
} from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { useSearchParams } from "react-router-dom";
import { useEffect } from "react";

const ReportsPage = () => {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("current-stock");
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [startDateText, setStartDateText] = useState("");
  const [endDate, setEndDate] = useState(null);
  const [endDateText, setEndDateText] = useState("");
  const [expiringDays, setExpiringDays] = useState("30");
  const [editingExpiration, setEditingExpiration] = useState(null);
  const [newExpirationDate, setNewExpirationDate] = useState("");

  // Handle date text input
  const handleDateTextChange = (text, setDate, setDateText) => {
    // Remove non-digits
    let digits = text.replace(/\D/g, '');
    
    // Limit to 8 digits
    digits = digits.slice(0, 8);
    
    // Format with slashes
    let formatted = '';
    if (digits.length > 0) {
      formatted = digits.slice(0, 2);
    }
    if (digits.length > 2) {
      formatted += '/' + digits.slice(2, 4);
    }
    if (digits.length > 4) {
      formatted += '/' + digits.slice(4, 8);
    }
    
    setDateText(formatted);
    
    // Parse complete date
    if (digits.length === 8) {
      const day = parseInt(digits.slice(0, 2));
      const month = parseInt(digits.slice(2, 4));
      const year = parseInt(digits.slice(4, 8));
      
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 1900) {
        const date = new Date(year, month - 1, day);
        if (!isNaN(date.getTime()) && date.getDate() === day) {
          setDate(date);
        }
      }
    }
  };

  // Handle calendar selection
  const handleCalendarSelect = (date, setDate, setDateText) => {
    setDate(date);
    if (date) {
      setDateText(format(date, "dd/MM/yyyy", { locale: ptBR }));
    } else {
      setDateText("");
    }
  };

  // Read tab from URL params on mount
  useEffect(() => {
    const urlTab = searchParams.get("tab");
    if (urlTab) {
      setActiveTab(urlTab);
      // Auto-fetch the report for certain tabs
      if (urlTab === "current-stock") {
        fetchReport("current-stock");
      } else if (urlTab === "expired") {
        fetchReport("expired");
      } else if (urlTab === "stock-value") {
        fetchReport("stock-value");
      } else if (urlTab === "expiring") {
        fetchReport("expiring", { days: "30" });
      }
    } else {
      // Default behavior - fetch current stock
      fetchReport("current-stock");
    }
  }, [searchParams]);

  const fetchReport = async (endpoint, params = {}) => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value) queryParams.append(key, value);
      });
      
      const response = await api.get(`/reports/${endpoint}?${queryParams.toString()}`);
      setReportData(response.data);
    } catch (error) {
      toast.error("Erro ao gerar relatório");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setReportData(null);
    
    // Auto-fetch for some reports
    if (tab === "current-stock") {
      fetchReport("current-stock");
    } else if (tab === "expired") {
      fetchReport("expired");
    } else if (tab === "stock-value") {
      fetchReport("stock-value");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    try {
      return format(new Date(dateStr), "dd/MM/yyyy", { locale: ptBR });
    } catch {
      return dateStr;
    }
  };

  const getDateRangeText = () => {
    if (startDate && endDate) {
      return `Período: ${formatDate(startDate)} a ${formatDate(endDate)}`;
    } else if (startDate) {
      return `A partir de: ${formatDate(startDate)}`;
    } else if (endDate) {
      return `Até: ${formatDate(endDate)}`;
    }
    return "";
  };

  // Export to Excel
  const exportToExcel = (data, filename, columns) => {
    if (!data || data.length === 0) {
      toast.error("Sem dados para exportar");
      return;
    }

    const worksheetData = data.map(row => {
      const obj = {};
      columns.forEach(col => {
        obj[col.header] = row[col.key] || "";
      });
      return obj;
    });

    const worksheet = XLSX.utils.json_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Relatório");

    // Auto-adjust column widths
    const colWidths = columns.map(col => ({
      wch: Math.max(col.header.length, 15)
    }));
    worksheet["!cols"] = colWidths;

    XLSX.writeFile(workbook, `${filename}_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
    toast.success("Relatório Excel exportado com sucesso!");
  };

  // Export to PDF
  const exportToPDF = (data, filename, columns, title) => {
    if (!data || data.length === 0) {
      toast.error("Sem dados para exportar");
      return;
    }

    const doc = new jsPDF();
    
    // Header
    doc.setFontSize(18);
    doc.setTextColor(31, 41, 55);
    doc.text(title, 14, 22);
    
    // Date range
    doc.setFontSize(10);
    doc.setTextColor(107, 114, 128);
    const dateText = getDateRangeText() || `Gerado em: ${format(new Date(), "dd/MM/yyyy HH:mm")}`;
    doc.text(dateText, 14, 30);

    // Table
    const tableData = data.map(row => 
      columns.map(col => {
        const value = row[col.key];
        if (col.format === "currency") return formatCurrency(value);
        if (col.format === "date") return formatDate(value);
        return value || "";
      })
    );

    autoTable(doc, {
      head: [columns.map(col => col.header)],
      body: tableData,
      startY: 38,
      styles: { 
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: { 
        fillColor: [20, 184, 166],
        textColor: 255,
        fontStyle: "bold"
      },
      alternateRowStyles: { fillColor: [248, 250, 252] },
      margin: { left: 14, right: 14 },
    });

    // Footer
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(156, 163, 175);
      doc.text(
        `Página ${i} de ${pageCount} - EstoqueClinic`,
        doc.internal.pageSize.getWidth() / 2,
        doc.internal.pageSize.getHeight() - 10,
        { align: "center" }
      );
    }

    doc.save(`${filename}_${format(new Date(), "yyyy-MM-dd")}.pdf`);
    toast.success("Relatório PDF exportado com sucesso!");
  };

  // Export buttons component
  const ExportButtons = ({ data, filename, columns, title }) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={!data || data.length === 0}
          className="gap-2"
        >
          <Download className="h-4 w-4" />
          Exportar
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => exportToExcel(data, filename, columns)}>
          <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
          Exportar Excel
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => exportToPDF(data, filename, columns, title)}>
          <File className="h-4 w-4 mr-2 text-red-600" />
          Exportar PDF
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // Date filter component
  const DateFilters = ({ onGenerate, showDaysFilter = false }) => (
    <div className="flex flex-wrap items-center gap-2 md:gap-4">
      <div className="flex items-center gap-2">
        <Label className="text-sm whitespace-nowrap">De:</Label>
        <div className="flex gap-1">
          <Input
            type="text"
            placeholder="DD/MM/AAAA"
            value={startDateText}
            onChange={(e) => handleDateTextChange(e.target.value, setStartDate, setStartDateText)}
            className="w-[120px] h-9 text-sm"
          />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9">
                <CalendarIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={startDate}
                onSelect={(date) => handleCalendarSelect(date, setStartDate, setStartDateText)}
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Label className="text-sm whitespace-nowrap">Até:</Label>
        <div className="flex gap-1">
          <Input
            type="text"
            placeholder="DD/MM/AAAA"
            value={endDateText}
            onChange={(e) => handleDateTextChange(e.target.value, setEndDate, setEndDateText)}
            className="w-[120px] h-9 text-sm"
          />
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon" className="h-9 w-9">
                <CalendarIcon className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
              <Calendar
                mode="single"
                selected={endDate}
                onSelect={(date) => handleCalendarSelect(date, setEndDate, setEndDateText)}
                locale={ptBR}
              />
            </PopoverContent>
          </Popover>
        </div>
      </div>
      {showDaysFilter && (
        <div className="flex items-center gap-2">
          <Label className="text-sm">Próximos:</Label>
          <Select value={expiringDays} onValueChange={setExpiringDays}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">7 dias</SelectItem>
              <SelectItem value="15">15 dias</SelectItem>
              <SelectItem value="30">30 dias</SelectItem>
              <SelectItem value="60">60 dias</SelectItem>
              <SelectItem value="90">90 dias</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <Button onClick={onGenerate} size="sm">
        Gerar Relatório
      </Button>
      {(startDate || endDate) && (
        <Button 
          variant="ghost" 
          size="sm"
          onClick={() => {
            setStartDate(null);
            setStartDateText("");
            setEndDate(null);
            setEndDateText("");
          }}
        >
          Limpar Datas
        </Button>
      )}
    </div>
  );

  // Column definitions for each report
  const currentStockColumns = [
    { header: "Produto", key: "name" },
    { header: "Categoria", key: "category" },
    { header: "Unidade", key: "unit" },
    { header: "Estoque", key: "current_stock" },
  ];

  const expiringColumns = [
    { header: "Produto", key: "product_name" },
    { header: "Categoria", key: "category" },
    { header: "Validade", key: "expiration_date", format: "date" },
    { header: "Dias Restantes", key: "days_until_expiration" },
    { header: "Quantidade", key: "quantity" },
  ];

  const expiredColumns = [
    { header: "Produto", key: "product_name" },
    { header: "Categoria", key: "category" },
    { header: "Validade", key: "expiration_date", format: "date" },
    { header: "Quantidade", key: "quantity" },
  ];

  const mostPurchasedColumns = [
    { header: "Produto", key: "product_name" },
    { header: "Categoria", key: "category" },
    { header: "Qtd Total", key: "total_quantity" },
    { header: "Custo Total", key: "total_cost", format: "currency" },
    { header: "Entradas", key: "entry_count" },
  ];

  const stockValueColumns = [
    { header: "Produto", key: "product_name" },
    { header: "Categoria", key: "category" },
    { header: "Estoque", key: "current_stock" },
    { header: "Custo Médio", key: "average_cost", format: "currency" },
    { header: "Valor Total", key: "total_value", format: "currency" },
  ];

  const exitsPeriodColumns = [
    { header: "Produto", key: "product_name" },
    { header: "Categoria", key: "category" },
    { header: "Quantidade", key: "quantity" },
    { header: "Motivo", key: "reason" },
    { header: "Responsável", key: "created_by" },
    { header: "Data", key: "created_at", format: "datetime" },
  ];

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
          Relatórios
        </h1>
        <p className="text-slate-500 mt-1">
          Analise os dados do seu estoque com filtros por período
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 h-auto gap-2">
          <TabsTrigger value="current-stock" className="gap-2" data-testid="tab-current-stock">
            <Package className="h-4 w-4" />
            <span className="hidden md:inline">Estoque Atual</span>
          </TabsTrigger>
          <TabsTrigger value="expiring" className="gap-2" data-testid="tab-expiring">
            <Clock className="h-4 w-4" />
            <span className="hidden md:inline">A Vencer</span>
          </TabsTrigger>
          <TabsTrigger value="expired" className="gap-2" data-testid="tab-expired">
            <XCircle className="h-4 w-4" />
            <span className="hidden md:inline">Vencidos</span>
          </TabsTrigger>
          <TabsTrigger value="entries-period" className="gap-2" data-testid="tab-entries-period">
            <ArrowDownToLine className="h-4 w-4" />
            <span className="hidden md:inline">Entradas</span>
          </TabsTrigger>
          <TabsTrigger value="exits-period" className="gap-2" data-testid="tab-exits-period">
            <ArrowUpFromLine className="h-4 w-4" />
            <span className="hidden md:inline">Saidas</span>
          </TabsTrigger>
          <TabsTrigger value="most-purchased" className="gap-2" data-testid="tab-most-purchased">
            <TrendingUp className="h-4 w-4" />
            <span className="hidden md:inline">Mais Comprados</span>
          </TabsTrigger>
          <TabsTrigger value="stock-value" className="gap-2" data-testid="tab-stock-value">
            <DollarSign className="h-4 w-4" />
            <span className="hidden md:inline">Valor do Estoque</span>
          </TabsTrigger>
          <TabsTrigger value="consumption-comparison" className="gap-2" data-testid="tab-consumption-comparison">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden md:inline">Comp. Entradas</span>
          </TabsTrigger>
          <TabsTrigger value="exits-comparison" className="gap-2" data-testid="tab-exits-comparison">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden md:inline">Comp. Saidas</span>
          </TabsTrigger>
        </TabsList>

        {/* Current Stock */}
        <TabsContent value="current-stock">
          <Card className="card-dashboard">
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading">Estoque Atual</CardTitle>
                    <CardDescription>
                      Visão completa de todos os produtos em estoque
                    </CardDescription>
                  </div>
                  <ExportButtons 
                    data={reportData} 
                    filename="estoque_atual" 
                    columns={currentStockColumns}
                    title="Relatório de Estoque Atual"
                  />
                </div>
                <DateFilters 
                  onGenerate={() => fetchReport("current-stock", {
                    start_date: startDate?.toISOString().split('T')[0],
                    end_date: endDate?.toISOString().split('T')[0],
                  })}
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : reportData && reportData.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produto</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead>Unidade</TableHead>
                        <TableHead className="text-right">Estoque</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.map((item) => (
                        <TableRow key={item.id} className="table-row-zebra">
                          <TableCell className="font-medium">{item.name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{item.category}</Badge>
                          </TableCell>
                          <TableCell>{item.unit}</TableCell>
                          <TableCell className="text-right font-semibold">
                            {item.current_stock}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <FileText className="h-12 w-12 mb-4 text-slate-300" />
                  <p>Nenhum dado disponível</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expiring */}
        <TabsContent value="expiring">
          <Card className="card-dashboard">
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading">Produtos a Vencer</CardTitle>
                    <CardDescription>
                      Produtos que vencem nos próximos dias
                    </CardDescription>
                  </div>
                  <ExportButtons 
                    data={reportData} 
                    filename="produtos_a_vencer" 
                    columns={expiringColumns}
                    title="Relatório de Produtos a Vencer"
                  />
                </div>
                <DateFilters 
                  showDaysFilter={true}
                  onGenerate={() => fetchReport("expiring", { 
                    days: expiringDays,
                    start_date: startDate?.toISOString().split('T')[0],
                    end_date: endDate?.toISOString().split('T')[0],
                  })}
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : reportData && reportData.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produto</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead>Validade</TableHead>
                        <TableHead>Dias Restantes</TableHead>
                        <TableHead className="text-right">Quantidade</TableHead>
                        <TableHead className="text-center w-20">Editar</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.map((item, idx) => (
                        <TableRow key={idx} className="table-row-zebra">
                          <TableCell className="font-medium">{item.product_name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{item.category}</Badge>
                          </TableCell>
                          <TableCell>
                            {editingExpiration === item.id ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  type="date"
                                  value={newExpirationDate}
                                  onChange={(e) => setNewExpirationDate(e.target.value)}
                                  className="h-8 w-36 text-sm"
                                />
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-green-600"
                                  onClick={async () => {
                                    try {
                                      await api.put(`/stock-entries/${item.id}/expiration`, {
                                        expiration_date: newExpirationDate
                                      });
                                      toast.success("Data atualizada!");
                                      setEditingExpiration(null);
                                      fetchReport("expiring", { days: expiringDays });
                                    } catch (error) {
                                      toast.error("Erro ao atualizar data");
                                    }
                                  }}
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-red-500"
                                  onClick={() => setEditingExpiration(null)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              <span>{formatDate(item.expiration_date)}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge
                              className={
                                item.days_until_expiration <= 7
                                  ? "alert-badge-danger"
                                  : item.days_until_expiration <= 15
                                  ? "alert-badge-warning"
                                  : "alert-badge-info"
                              }
                            >
                              {item.days_until_expiration} dias
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {item.quantity}
                          </TableCell>
                          <TableCell className="text-center">
                            {editingExpiration !== item.id && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => {
                                  setEditingExpiration(item.id);
                                  const dateStr = item.expiration_date?.split("T")[0] || "";
                                  setNewExpirationDate(dateStr);
                                }}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <FileText className="h-12 w-12 mb-4 text-slate-300" />
                  <p>Clique em "Gerar Relatório" para ver os dados</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Expired */}
        <TabsContent value="expired">
          <Card className="card-dashboard">
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading">Produtos Vencidos</CardTitle>
                    <CardDescription>
                      Produtos com validade expirada
                    </CardDescription>
                  </div>
                  <ExportButtons 
                    data={reportData} 
                    filename="produtos_vencidos" 
                    columns={expiredColumns}
                    title="Relatório de Produtos Vencidos"
                  />
                </div>
                <DateFilters 
                  onGenerate={() => fetchReport("expired", {
                    start_date: startDate?.toISOString().split('T')[0],
                    end_date: endDate?.toISOString().split('T')[0],
                  })}
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : reportData && reportData.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produto</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead>Validade</TableHead>
                        <TableHead className="text-right">Quantidade</TableHead>
                        <TableHead className="text-center w-20">Editar</TableHead>
                        <TableHead className="text-center w-20">Excluir</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.map((item, idx) => (
                        <TableRow key={idx} className="table-row-zebra bg-red-50/50">
                          <TableCell className="font-medium">{item.product_name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{item.category}</Badge>
                          </TableCell>
                          <TableCell>
                            {editingExpiration === item.id ? (
                              <div className="flex items-center gap-1">
                                <Input
                                  type="date"
                                  value={newExpirationDate}
                                  onChange={(e) => setNewExpirationDate(e.target.value)}
                                  className="h-8 w-36 text-sm"
                                  data-testid={`edit-expiration-input-${idx}`}
                                />
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-green-600"
                                  onClick={async () => {
                                    try {
                                      await api.put(`/stock-entries/${item.id}/expiration`, {
                                        expiration_date: newExpirationDate
                                      });
                                      toast.success("Data atualizada!");
                                      setEditingExpiration(null);
                                      fetchReport("expired");
                                    } catch (error) {
                                      toast.error("Erro ao atualizar data");
                                    }
                                  }}
                                  data-testid={`save-expiration-btn-${idx}`}
                                >
                                  <Check className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-7 w-7 text-red-500"
                                  onClick={() => setEditingExpiration(null)}
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              <span className="text-red-600">{formatDate(item.expiration_date)}</span>
                            )}
                          </TableCell>
                          <TableCell className="text-right font-semibold text-red-600">
                            {item.quantity}
                          </TableCell>
                          <TableCell className="text-center">
                            {editingExpiration !== item.id && (
                              <Button
                                size="icon"
                                variant="ghost"
                                className="h-8 w-8"
                                onClick={() => {
                                  setEditingExpiration(item.id);
                                  const dateStr = item.expiration_date?.split("T")[0] || "";
                                  setNewExpirationDate(dateStr);
                                }}
                                data-testid={`edit-expiration-btn-${idx}`}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                          </TableCell>
                          <TableCell className="text-center">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50"
                              onClick={async () => {
                                if (window.confirm(`Descartar ${item.quantity} unidades de "${item.product_name}"? Essa quantidade sera removida do estoque atual.`)) {
                                  try {
                                    await api.delete(`/stock-entries/${item.id}/discard`);
                                    toast.success(`${item.quantity} unidades descartadas do estoque`);
                                    fetchReport("expired");
                                  } catch (error) {
                                    toast.error("Erro ao descartar item");
                                  }
                                }
                              }}
                              data-testid={`discard-expired-btn-${idx}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <FileText className="h-12 w-12 mb-4 text-slate-300" />
                  <p>Nenhum produto vencido</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Most Purchased */}
        <TabsContent value="most-purchased">
          <Card className="card-dashboard">
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading">Produtos Mais Comprados</CardTitle>
                    <CardDescription>
                      Ranking de produtos por quantidade adquirida
                    </CardDescription>
                  </div>
                  <ExportButtons 
                    data={reportData} 
                    filename="mais_comprados" 
                    columns={mostPurchasedColumns}
                    title="Relatório de Produtos Mais Comprados"
                  />
                </div>
                <DateFilters 
                  onGenerate={() => fetchReport("most-purchased", {
                    start_date: startDate?.toISOString().split('T')[0],
                    end_date: endDate?.toISOString().split('T')[0],
                  })}
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : reportData && reportData.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-12">#</TableHead>
                        <TableHead>Produto</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead className="text-right">Qtd Total</TableHead>
                        <TableHead className="text-right">Custo Total</TableHead>
                        <TableHead className="text-right">Entradas</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.map((item, idx) => (
                        <TableRow key={idx} className="table-row-zebra">
                          <TableCell className="font-bold text-primary">{idx + 1}</TableCell>
                          <TableCell className="font-medium">{item.product_name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{item.category}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-emerald-600">
                            {item.total_quantity}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(item.total_cost)}
                          </TableCell>
                          <TableCell className="text-right">{item.entry_count}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <FileText className="h-12 w-12 mb-4 text-slate-300" />
                  <p>Clique em "Gerar Relatório" para ver os dados</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Entries Period */}
        <TabsContent value="entries-period">
          <Card className="card-dashboard">
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading">Entradas do Periodo</CardTitle>
                    <CardDescription>
                      Entradas de estoque filtradas por periodo
                    </CardDescription>
                  </div>
                  <ExportButtons
                    data={reportData?.items}
                    filename="entradas_periodo"
                    columns={[
                      { header: "Produto", key: "product_name" },
                      { header: "Fornecedor", key: "supplier_name" },
                      { header: "Quantidade", key: "quantity" },
                      { header: "Custo Unit.", key: "unit_cost", format: "currency" },
                      { header: "Total", key: "total_item", format: "currency" },
                      { header: "Data", key: "created_at", format: "date" },
                    ]}
                    title="Relatorio de Entradas"
                  />
                </div>
                <DateFilters
                  onGenerate={() => fetchReport("entries-period", {
                    start_date: startDate?.toISOString().split('T')[0],
                    end_date: endDate?.toISOString().split('T')[0],
                  })}
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : reportData?.items && reportData.items.length > 0 ? (
                <div>
                  <div className="mb-4 flex items-center gap-4 text-sm text-slate-500">
                    <span><strong>{reportData.count}</strong> entradas</span>
                    <span>Qtd total: <strong>{reportData.total_quantity}</strong></span>
                    <span>Custo total: <strong>{formatCurrency(reportData.total_cost)}</strong></span>
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Produto</TableHead>
                          <TableHead>Fornecedor</TableHead>
                          <TableHead className="text-right">Qtd</TableHead>
                          <TableHead className="text-right">Custo Unit.</TableHead>
                          <TableHead className="text-right">Total</TableHead>
                          <TableHead>Responsavel</TableHead>
                          <TableHead>Data</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reportData.items.map((item, idx) => (
                          <TableRow key={idx} className="table-row-zebra">
                            <TableCell className="font-medium">{item.product_name}</TableCell>
                            <TableCell>{item.supplier_name}</TableCell>
                            <TableCell className="text-right font-semibold">{item.quantity}</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.unit_cost)}</TableCell>
                            <TableCell className="text-right font-semibold">{formatCurrency(item.total_item)}</TableCell>
                            <TableCell className="text-sm">{item.created_by}</TableCell>
                            <TableCell className="text-sm">{formatDate(item.created_at)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <FileText className="h-12 w-12 mb-4 text-slate-300" />
                  <p>Selecione um periodo e clique em "Gerar Relatorio"</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exits Period */}
        <TabsContent value="exits-period">
          <Card className="card-dashboard">
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading">Saídas do Período</CardTitle>
                    <CardDescription>
                      Itens que saíram do estoque no período selecionado
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-4">
                    {reportData?.total_quantity !== undefined && (
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Total de Itens</p>
                        <p className="text-xl font-bold text-red-600">
                          {reportData.total_quantity} ({reportData.count} saídas)
                        </p>
                      </div>
                    )}
                    <ExportButtons 
                      data={reportData?.items} 
                      filename="saidas_periodo" 
                      columns={exitsPeriodColumns}
                      title="Relatório de Saídas do Período"
                    />
                  </div>
                </div>
                <DateFilters 
                  onGenerate={() => fetchReport("exits-period", {
                    start_date: startDate?.toISOString().split('T')[0],
                    end_date: endDate?.toISOString().split('T')[0],
                  })}
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : reportData?.items && reportData.items.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produto</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead className="text-right">Qtd</TableHead>
                        <TableHead>Motivo</TableHead>
                        <TableHead>Responsável</TableHead>
                        <TableHead>Data</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.items.map((item, idx) => (
                        <TableRow key={idx} className="table-row-zebra">
                          <TableCell className="font-medium">{item.product_name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{item.category}</Badge>
                          </TableCell>
                          <TableCell className="text-right font-semibold text-red-600">
                            -{item.quantity}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{item.reason}</Badge>
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {item.created_by}
                          </TableCell>
                          <TableCell className="text-sm text-slate-500">
                            {formatDate(item.created_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <FileText className="h-12 w-12 mb-4 text-slate-300" />
                  <p>Selecione um período e clique em "Gerar Relatório"</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stock Value */}
        <TabsContent value="stock-value">
          <Card className="card-dashboard">
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading">Valor do Estoque</CardTitle>
                    <CardDescription>
                      Análise financeira do estoque
                    </CardDescription>
                  </div>
                  <div className="flex items-center gap-4">
                    {reportData?.total_value && (
                      <div className="text-right">
                        <p className="text-sm text-slate-500">Valor Total</p>
                        <p className="text-xl font-bold text-primary">
                          {formatCurrency(reportData.total_value)}
                        </p>
                      </div>
                    )}
                    <ExportButtons 
                      data={reportData?.items} 
                      filename="valor_estoque" 
                      columns={stockValueColumns}
                      title="Relatório de Valor do Estoque"
                    />
                  </div>
                </div>
                <DateFilters 
                  onGenerate={() => fetchReport("stock-value", {
                    start_date: startDate?.toISOString().split('T')[0],
                    end_date: endDate?.toISOString().split('T')[0],
                  })}
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : reportData?.items && reportData.items.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produto</TableHead>
                        <TableHead>Categoria</TableHead>
                        <TableHead className="text-right">Estoque</TableHead>
                        <TableHead className="text-right">Custo Médio</TableHead>
                        <TableHead className="text-right">Valor Total</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.items.map((item, idx) => (
                        <TableRow key={idx} className="table-row-zebra">
                          <TableCell className="font-medium">{item.product_name}</TableCell>
                          <TableCell>
                            <Badge variant="secondary">{item.category}</Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            {item.current_stock} {item.unit}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatCurrency(item.average_cost)}
                          </TableCell>
                          <TableCell className="text-right font-semibold">
                            {formatCurrency(item.total_value)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <FileText className="h-12 w-12 mb-4 text-slate-300" />
                  <p>Nenhum dado disponível</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Consumption Comparison */}
        <TabsContent value="consumption-comparison">
          <Card className="card-dashboard">
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading">Comparativo de Entradas Mensal</CardTitle>
                    <CardDescription>
                      Compare as entradas de cada produto mes a mes
                    </CardDescription>
                  </div>
                  {reportData?.items && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2">
                          <Download className="h-4 w-4" />
                          Exportar
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => {
                          if (!reportData?.items || !reportData?.months) return;
                          const rows = reportData.items.map(item => {
                            const row = { "Produto": item.product_name, "Categoria": item.category };
                            reportData.months.forEach(m => {
                              row[reportData.month_labels[m]] = item.months[m] || 0;
                            });
                            row["Total"] = item.total;
                            return row;
                          });
                          const ws = XLSX.utils.json_to_sheet(rows);
                          const wb = XLSX.utils.book_new();
                          XLSX.utils.book_append_sheet(wb, ws, "Comparativo");
                          XLSX.writeFile(wb, `comparativo_consumo_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
                          toast.success("Excel exportado!");
                        }}>
                          <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                          Exportar Excel
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                          if (!reportData?.items || !reportData?.months) return;
                          const doc = new jsPDF({ orientation: "landscape" });
                          doc.setFontSize(16);
                          doc.text("Comparativo de Consumo Mensal", 14, 18);
                          doc.setFontSize(9);
                          doc.setTextColor(107, 114, 128);
                          doc.text(getDateRangeText() || `Gerado em: ${format(new Date(), "dd/MM/yyyy HH:mm")}`, 14, 25);
                          const heads = ["Produto", ...reportData.months.map(m => reportData.month_labels[m]), "Total"];
                          const body = reportData.items.map(item => [
                            item.product_name,
                            ...reportData.months.map(m => item.months[m] || 0),
                            item.total
                          ]);
                          autoTable(doc, {
                            head: [heads], body, startY: 30,
                            styles: { fontSize: 7, cellPadding: 2 },
                            headStyles: { fillColor: [20, 184, 166], textColor: 255, fontStyle: "bold" },
                            alternateRowStyles: { fillColor: [248, 250, 252] },
                          });
                          doc.save(`comparativo_consumo_${format(new Date(), "yyyy-MM-dd")}.pdf`);
                          toast.success("PDF exportado!");
                        }}>
                          <File className="h-4 w-4 mr-2 text-red-600" />
                          Exportar PDF
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                <DateFilters 
                  onGenerate={() => fetchReport("consumption-comparison", {
                    start_date: startDate?.toISOString().split('T')[0],
                    end_date: endDate?.toISOString().split('T')[0],
                  })}
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : reportData?.items && reportData.items.length > 0 && reportData.months ? (
                <div>
                  <div className="mb-4 flex items-center gap-4 text-sm text-slate-500">
                    <span><strong>{reportData.total_items}</strong> produtos</span>
                    <span><strong>{reportData.months.length}</strong> meses</span>
                    {getDateRangeText() && <span>{getDateRangeText()}</span>}
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="sticky left-0 bg-white z-10 min-w-[200px]">Produto</TableHead>
                          <TableHead>Categoria</TableHead>
                          {reportData.months.map(m => (
                            <TableHead key={m} className="text-center min-w-[80px]">
                              {reportData.month_labels[m]}
                            </TableHead>
                          ))}
                          <TableHead className="text-center font-bold">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reportData.items.map((item, idx) => (
                          <TableRow key={idx} className="table-row-zebra">
                            <TableCell className="sticky left-0 bg-white font-medium">{item.product_name}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs">{item.category}</Badge>
                            </TableCell>
                            {reportData.months.map(m => {
                              const val = item.months[m] || 0;
                              const maxVal = Math.max(...reportData.months.map(mm => item.months[mm] || 0));
                              const isMax = val > 0 && val === maxVal;
                              return (
                                <TableCell key={m} className={`text-center ${val === 0 ? "text-slate-300" : isMax ? "font-bold text-primary" : ""}`}>
                                  {val}
                                </TableCell>
                              );
                            })}
                            <TableCell className="text-center font-bold text-slate-900">
                              {item.total}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <BarChart3 className="h-12 w-12 mb-4 text-slate-300" />
                  <p>Selecione um periodo e clique em "Gerar Relatorio"</p>
                  <p className="text-sm mt-1">Ex: Janeiro a Marco para comparar o consumo mes a mes</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exits Comparison */}
        <TabsContent value="exits-comparison">
          <Card className="card-dashboard">
            <CardHeader>
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="font-heading">Comparativo de Saidas Mensal</CardTitle>
                    <CardDescription>
                      Compare as saidas de cada produto mes a mes
                    </CardDescription>
                  </div>
                  {reportData?.items && reportData?.months && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm" className="gap-2">
                          <Download className="h-4 w-4" />
                          Exportar
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => {
                          if (!reportData?.items || !reportData?.months) return;
                          const rows = reportData.items.map(item => {
                            const row = { "Produto": item.product_name, "Categoria": item.category };
                            reportData.months.forEach(m => {
                              row[reportData.month_labels[m]] = item.months[m] || 0;
                            });
                            row["Total"] = item.total;
                            return row;
                          });
                          const ws = XLSX.utils.json_to_sheet(rows);
                          const wb = XLSX.utils.book_new();
                          XLSX.utils.book_append_sheet(wb, ws, "Comp Saidas");
                          XLSX.writeFile(wb, `comparativo_saidas_${format(new Date(), "yyyy-MM-dd")}.xlsx`);
                          toast.success("Excel exportado!");
                        }}>
                          <FileSpreadsheet className="h-4 w-4 mr-2 text-green-600" />
                          Exportar Excel
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => {
                          if (!reportData?.items || !reportData?.months) return;
                          const doc = new jsPDF({ orientation: "landscape" });
                          doc.setFontSize(16);
                          doc.text("Comparativo de Saidas Mensal", 14, 18);
                          doc.setFontSize(9);
                          doc.setTextColor(107, 114, 128);
                          doc.text(getDateRangeText() || `Gerado em: ${format(new Date(), "dd/MM/yyyy HH:mm")}`, 14, 25);
                          const heads = ["Produto", ...reportData.months.map(m => reportData.month_labels[m]), "Total"];
                          const body = reportData.items.map(item => [
                            item.product_name,
                            ...reportData.months.map(m => item.months[m] || 0),
                            item.total
                          ]);
                          autoTable(doc, {
                            head: [heads], body, startY: 30,
                            styles: { fontSize: 7, cellPadding: 2 },
                            headStyles: { fillColor: [20, 184, 166], textColor: 255, fontStyle: "bold" },
                            alternateRowStyles: { fillColor: [248, 250, 252] },
                          });
                          doc.save(`comparativo_saidas_${format(new Date(), "yyyy-MM-dd")}.pdf`);
                          toast.success("PDF exportado!");
                        }}>
                          <File className="h-4 w-4 mr-2 text-red-600" />
                          Exportar PDF
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
                <DateFilters
                  onGenerate={() => fetchReport("exits-comparison", {
                    start_date: startDate?.toISOString().split('T')[0],
                    end_date: endDate?.toISOString().split('T')[0],
                  })}
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center h-64">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                </div>
              ) : reportData?.items && reportData.items.length > 0 && reportData.months ? (
                <div>
                  <div className="mb-4 flex items-center gap-4 text-sm text-slate-500">
                    <span><strong>{reportData.total_items}</strong> produtos</span>
                    <span><strong>{reportData.months.length}</strong> meses</span>
                    {getDateRangeText() && <span>{getDateRangeText()}</span>}
                  </div>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="sticky left-0 bg-white z-10 min-w-[200px]">Produto</TableHead>
                          <TableHead>Categoria</TableHead>
                          {reportData.months.map(m => (
                            <TableHead key={m} className="text-center min-w-[80px]">
                              {reportData.month_labels[m]}
                            </TableHead>
                          ))}
                          <TableHead className="text-center font-bold">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {reportData.items.map((item, idx) => (
                          <TableRow key={idx} className="table-row-zebra">
                            <TableCell className="sticky left-0 bg-white font-medium">{item.product_name}</TableCell>
                            <TableCell>
                              <Badge variant="secondary" className="text-xs">{item.category}</Badge>
                            </TableCell>
                            {reportData.months.map(m => {
                              const val = item.months[m] || 0;
                              const maxVal = Math.max(...reportData.months.map(mm => item.months[mm] || 0));
                              const isMax = val > 0 && val === maxVal;
                              return (
                                <TableCell key={m} className={`text-center ${val === 0 ? "text-slate-300" : isMax ? "font-bold text-red-600" : ""}`}>
                                  {val}
                                </TableCell>
                              );
                            })}
                            <TableCell className="text-center font-bold text-slate-900">
                              {item.total}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                  <BarChart3 className="h-12 w-12 mb-4 text-slate-300" />
                  <p>Selecione um periodo e clique em "Gerar Relatorio"</p>
                  <p className="text-sm mt-1">Ex: Janeiro a Marco para comparar as saidas mes a mes</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsPage;
